import { Dispatch, FunctionComponent, SetStateAction, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useViewport } from 'reactflow'

import { Icon, Typography } from '@matillion/component-library'

import { ReactComponent as MinusSvg } from 'assets/minus-small.svg'

import { HorizontalStack } from 'components/HorizontalStack/HorizontalStack'
import { IconButton } from 'components/IconButton/IconButton'

import config from 'config'

import { useEtlFlow } from '../../hooks/useEtlFlow'
import classes from './ZoomToolbar.module.scss'

interface ZoomToolbarProps {
  snapEnabled: boolean
  setSnapEnabled: Dispatch<SetStateAction<boolean>>
}
export const ZoomToolbar: FunctionComponent<ZoomToolbarProps> = ({
  snapEnabled,
  setSnapEnabled
}) => {
  const { zoomTo, fitView } = useEtlFlow()
  const { zoom } = useViewport()
  const { t } = useTranslation()

  const onZoomOut = useCallback(() => {
    zoomTo(zoom - 0.25, { duration: 400 })
  }, [zoomTo, zoom])

  const onZoomIn = useCallback(() => {
    zoomTo(zoom + 0.25, { duration: 400 })
  }, [zoomTo, zoom])

  return (
    <div className={classes.ZoomToolbar}>
      <HorizontalStack withSpacer>
        <HorizontalStack>
          <IconButton
            label={t('canvasZoomControls.zoomOut')}
            onClick={onZoomOut}
            data-testid="canvas-zoom-controls-zoom-out"
          >
            <MinusSvg />
          </IconButton>

          <div
            aria-live="polite"
            data-testid="canvas-zoom-controls-percentage"
            className={classes.ZoomToolbar__Percentage}
          >
            <Typography>{(zoom * 100).toFixed(0)}%</Typography>
          </div>

          <IconButton
            label={t('canvasZoomControls.zoomIn')}
            onClick={onZoomIn}
            data-testid="canvas-zoom-controls-zoom-in"
          >
            <Icon.Add />
          </IconButton>
          <IconButton
            label={t('canvasZoomControls.fitAll')}
            onClick={() =>
              fitView({
                maxZoom: 1,
                duration: 400
              })
            }
            data-testid="canvas-zoom-controls-fit-all"
          >
            <Icon.FitAll />
          </IconButton>
          {snapEnabled ? (
            <IconButton
              data-testid="canvas-zoom-controls-snap2grid-enabled"
              onClick={() => setSnapEnabled(false)}
              label={t('canvasZoomControls.gridOff')}
            >
              <Icon.Grid />
            </IconButton>
          ) : (
            <IconButton
              data-testid="canvas-zoom-controls-snap2grid-disabled"
              onClick={() => setSnapEnabled(true)}
              label={t('canvasZoomControls.gridOn')}
            >
              <Icon.GridDisabled />
            </IconButton>
          )}
        </HorizontalStack>

        <a
          href={config.docs.designerOverviewUrl}
          rel="noopener noreferrer"
          target="_blank"
          data-testid="canvas-zoom-controls-help"
        >
          <IconButton label={t('common.help')}>
            <Icon.InfoNoBg />
          </IconButton>
        </a>
      </HorizontalStack>
    </div>
  )
}
